import React from 'react';

import Header, {MinimalHeader} from './Header';
import Footer, {MinimalFooter} from './Footer';
import AppShell from './navigation/AppShell';
import useSidebarNavEnabled from './navigation/useSidebarNav';
import PageBanner from './PageBanner';
import DocumentHeader from './DocumentHeader';
import GoogleOneTap from './GoogleOneTap';
import {NotificationContainer} from './Notification';
import ConfirmNewAccountModal from './ConfirmNewAccountModal';
import SpecialOfferModal from './SpecialOfferModal';
import AccountSharingModal from './AccountSharingModal';
import ImpersonationBanner from './ImpersonationBanner';
import DiscordConnectionModal from './DiscordConnectionModal';
import {useAppContext} from '../hooks';
import {cx} from '../util';

const PageContainer = ({
  stickyHeader = false,
  minimalHeader = false,
  hideHeader = false,
  initialCollapsed,
  // TODO: turn off all banners for PromoLandingPage and duet banner for /practice
  showPageBanner = true,
  hideFooter = false,
  minimalFooter,
  hideGoogleOneTap = false,
  children,
  metadata = {},
  exitEnabled = true,
  scrollEnabled = false,
  className,
  upgradeLink = '/upgrade?src=nav',
}) => {
  const {
    currentUser,
    features,
    hasSubscription,
    notifications,
    pendingAccount,
    loading,
  } = useAppContext();

  const showSpecialOffer = (
    !currentUser
    && !hasSubscription
    && !pendingAccount
    && features?.special_offer
    && features?.special_offer !== 'off'
  );

  const HeaderComponent = minimalHeader ? MinimalHeader : Header;
  const FooterComponent = minimalFooter ? MinimalFooter : Footer;
  // hideHeader: the route already knows the top header never applies, so we
  // never paint it while AppContext is still resolving the user.
  const sidebarEnabled = useSidebarNavEnabled();
  const useSidebarShell = (hideHeader || sidebarEnabled) && !minimalHeader;

  // Shared between both shells so the sidebar rollout can't drift from the
  // header on modals and banners.
  const overlays = (
    <>
      <GoogleOneTap show={!(hideGoogleOneTap || currentUser || loading)} />
      <AccountSharingModal />
      {pendingAccount && <ConfirmNewAccountModal />}
      {showSpecialOffer && (
        <SpecialOfferModal
          metadata={metadata}
          exitEnabled={exitEnabled}
          scrollEnabled={scrollEnabled}
        />
      )}
      <NotificationContainer notifications={notifications} />
      <DiscordConnectionModal />
    </>
  );

  if (useSidebarShell) {
    return (
      <>
        <DocumentHeader />
        <AppShell
          upgradeLink={upgradeLink}
          initialCollapsed={initialCollapsed}
          mainClassName={className}
          banners={(
            <>
              <ImpersonationBanner />
              {showPageBanner && <PageBanner />}
            </>
          )}
          // Members navigate from the sidebar, so the full sitemap footer is
          // redundant on every logged-in page. Keep only the legal strip.
          footer={!hideFooter && <MinimalFooter />}
        >
          {children}
        </AppShell>
        {overlays}
      </>
    );
  }

  return (
    <>
      <ImpersonationBanner />
      <DocumentHeader />
      {showPageBanner && <PageBanner />}
      <HeaderComponent isSticky={stickyHeader} upgradeLink={upgradeLink} />
      {overlays}
      <main id="main-content" tabIndex="-1" className={cx('min-h-screen', className)}>
        {children}
      </main>
      {!hideFooter && <FooterComponent />}
    </>
  );
};

export default PageContainer;
